import { FC, MouseEvent } from 'react';
import { useDisclosure } from '@chakra-ui/react';

import { formatAmount } from 'common/formatAmount';
import IconButton from 'components/_core/IconButton';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineDropdown from 'components/_core/SunshineDropdown';
import SunshineTag from 'components/_core/SunshineTag';

import { CatalogItem } from '../../../../../../../../hooks/useCatalogItemsQuery';
import ItemGrid from '../ItemGrid';
import locales from './locales';

interface CatalogItemCardProps {
  catalogItem: CatalogItem;
  onClickUpdate: () => void;
  onClickDelete: (catalogItemId: string) => void;
}

const CatalogItemCard: FC<CatalogItemCardProps> = ({
  catalogItem,
  onClickDelete,
  onClickUpdate,
}) => {
  const {
    isOpen: isMenuOpen,
    onClose: closeMenu,
    onToggle: toggleMenu,
  } = useDisclosure();

  const preventEventPropagation = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <SunshineCard onClick={onClickUpdate}>
      <ItemGrid>
        <SunshineCard.Content overflow="hidden">
          <SunshineCard.Title
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            width="100%"
          >
            {catalogItem.title}
          </SunshineCard.Title>

          {catalogItem.subtitle ? (
            <SunshineCard.Text
              overflow="hidden"
              size="md"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              width="100%"
            >
              {catalogItem.subtitle}
            </SunshineCard.Text>
          ) : null}
        </SunshineCard.Content>

        <SunshineTag>{locales.type[catalogItem.type]}</SunshineTag>

        <SunshineCard.Text align="right" size="md">
          {formatAmount({
            forcePlusSign: false,
            value: catalogItem.unitPrice,
          })}
        </SunshineCard.Text>

        <SunshineCard.Text
          align="right"
          size="md"
        >{`${catalogItem.vatPercentage}%`}</SunshineCard.Text>

        <SunshineDropdown isOpen={isMenuOpen} onClose={closeMenu}>
          <SunshineDropdown.Trigger
            onClick={(e) => {
              preventEventPropagation(e);
              toggleMenu();
            }}
          >
            <IconButton
              aria-label={locales.buttonAriaLabel}
              icon="vertical-ellipsis"
              margin="auto"
              variant="secondary"
            />
          </SunshineDropdown.Trigger>

          <SunshineDropdown.List>
            <SunshineDropdown.Item
              icon="edit"
              onClick={(e) => {
                preventEventPropagation(e);
                onClickUpdate();
              }}
            >
              {locales.edit}
            </SunshineDropdown.Item>

            <SunshineDropdown.Divider />

            <SunshineDropdown.Item
              colorScheme="red"
              icon="trash"
              onClick={(e) => {
                preventEventPropagation(e);
                onClickDelete(catalogItem.invoiceCatalogItemId);
              }}
            >
              {locales.delete}
            </SunshineDropdown.Item>
          </SunshineDropdown.List>
        </SunshineDropdown>
      </ItemGrid>
    </SunshineCard>
  );
};

export default CatalogItemCard;
